import React, { useState, useEffect, useRef } from 'react';
import { settingsAPI } from '../../api';
import { Loading } from '../../components';
import './Settings.css';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('branding');
  const [settings, setSettings] = useState({
    appName: '',
    logo: null,
    favicon: null,
    tagline: '',
    contactEmail: '',
    contactPhone: '',
    website: '',
    address: '',
    labAssistantName: '',
    labAssistantTitle: '',
    labAssistantSignature: null,
    labInchargeName: '',
    labInchargeTitle: '',
    labInchargeSignature: null,
    pdfWatermark: null
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [uploadingLabAssistantSig, setUploadingLabAssistantSig] = useState(false);
  const [uploadingLabInchargeSig, setUploadingLabInchargeSig] = useState(false);
  const [uploadingWatermark, setUploadingWatermark] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const fileInputRef = useRef(null);
  const faviconInputRef = useRef(null);
  const labAssistantSigRef = useRef(null);
  const labInchargeSigRef = useRef(null);
  const watermarkInputRef = useRef(null);

  const tabs = [
    { id: 'branding', label: 'Branding', icon: '🎨' },
    { id: 'pdf', label: 'PDF Settings', icon: '📄' },
    { id: 'general', label: 'General', icon: '⚙️' }
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.get();
      if (response.data?.data) {
        setSettings(response.data.data);
      }
    } catch (err) {
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setSaving(true);

    try {
      const { logo, favicon, labAssistantSignature, labInchargeSignature, pdfWatermark, ...updateData } = settings;
      await settingsAPI.update(updateData);
      setSuccess('Settings saved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload JPEG, PNG, SVG, or WebP.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('File too large. Maximum size is 5MB.');
      return;
    }

    setError(null);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await settingsAPI.uploadLogo(formData);
      if (response.data?.data?.logo) {
        setSettings(prev => ({ ...prev, logo: response.data.data.logo }));
        setSuccess('Logo uploaded successfully');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload logo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('Are you sure you want to delete the logo?')) return;

    setError(null);
    setUploading(true);

    try {
      await settingsAPI.deleteLogo();
      setSettings(prev => ({ ...prev, logo: null }));
      setSuccess('Logo deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete logo');
    } finally {
      setUploading(false);
    }
  };

  const handleFaviconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/x-icon', 'image/vnd.microsoft.icon', 'image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload ICO, PNG, JPEG, or SVG.');
      return;
    }

    if (file.size > 1 * 1024 * 1024) {
      setError('File too large. Maximum size is 1MB for favicon.');
      return;
    }

    setError(null);
    setUploadingFavicon(true);

    try {
      const formData = new FormData();
      formData.append('favicon', file);

      const response = await settingsAPI.uploadFavicon(formData);
      if (response.data?.data?.favicon) {
        setSettings(prev => ({ ...prev, favicon: response.data.data.favicon }));
        const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
        link.type = 'image/x-icon';
        link.rel = 'shortcut icon';
        link.href = response.data.data.favicon;
        document.getElementsByTagName('head')[0].appendChild(link);
        setSuccess('Favicon uploaded successfully');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload favicon');
    } finally {
      setUploadingFavicon(false);
      if (faviconInputRef.current) {
        faviconInputRef.current.value = '';
      }
    }
  };

  const handleDeleteFavicon = async () => {
    if (!window.confirm('Are you sure you want to delete the favicon?')) return;

    setError(null);
    setUploadingFavicon(true);

    try {
      await settingsAPI.deleteFavicon();
      setSettings(prev => ({ ...prev, favicon: null }));
      setSuccess('Favicon deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete favicon');
    } finally {
      setUploadingFavicon(false);
    }
  };

  const handleLabAssistantSigUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload JPEG, PNG, or WebP for signature.');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError('File too large. Maximum size is 2MB for signature.');
      return;
    }

    setError(null);
    setUploadingLabAssistantSig(true);

    try {
      const formData = new FormData();
      formData.append('signature', file);

      const response = await settingsAPI.uploadLabAssistantSignature(formData);
      if (response.data?.data?.labAssistantSignature) {
        setSettings(prev => ({ ...prev, labAssistantSignature: response.data.data.labAssistantSignature }));
        setSuccess('Lab Assistant signature uploaded successfully');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload signature');
    } finally {
      setUploadingLabAssistantSig(false);
      if (labAssistantSigRef.current) {
        labAssistantSigRef.current.value = '';
      }
    }
  };

  const handleDeleteLabAssistantSig = async () => {
    if (!window.confirm('Are you sure you want to delete the Lab Assistant signature?')) return;

    setError(null);
    setUploadingLabAssistantSig(true);

    try {
      await settingsAPI.deleteLabAssistantSignature();
      setSettings(prev => ({ ...prev, labAssistantSignature: null }));
      setSuccess('Lab Assistant signature deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete signature');
    } finally {
      setUploadingLabAssistantSig(false);
    }
  };

  const handleLabInchargeSigUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload JPEG, PNG, or WebP for signature.');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError('File too large. Maximum size is 2MB for signature.');
      return;
    }

    setError(null);
    setUploadingLabInchargeSig(true);

    try {
      const formData = new FormData();
      formData.append('signature', file);

      const response = await settingsAPI.uploadLabInchargeSignature(formData);
      if (response.data?.data?.labInchargeSignature) {
        setSettings(prev => ({ ...prev, labInchargeSignature: response.data.data.labInchargeSignature }));
        setSuccess('Lab Incharge signature uploaded successfully');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload signature');
    } finally {
      setUploadingLabInchargeSig(false);
      if (labInchargeSigRef.current) {
        labInchargeSigRef.current.value = '';
      }
    }
  };

  const handleDeleteLabInchargeSig = async () => {
    if (!window.confirm('Are you sure you want to delete the Lab Incharge signature?')) return;

    setError(null);
    setUploadingLabInchargeSig(true);

    try {
      await settingsAPI.deleteLabInchargeSignature();
      setSettings(prev => ({ ...prev, labInchargeSignature: null }));
      setSuccess('Lab Incharge signature deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete signature');
    } finally {
      setUploadingLabInchargeSig(false);
    }
  };

  const handleWatermarkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload JPEG, PNG, or WebP for watermark.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('File too large. Maximum size is 5MB for watermark.');
      return;
    }

    setError(null);
    setUploadingWatermark(true);

    try {
      const formData = new FormData();
      formData.append('watermark', file);

      const response = await settingsAPI.uploadPdfWatermark(formData);
      if (response.data?.data?.pdfWatermark) {
        setSettings(prev => ({ ...prev, pdfWatermark: response.data.data.pdfWatermark }));
        setSuccess('PDF watermark uploaded successfully');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload watermark');
    } finally {
      setUploadingWatermark(false);
      if (watermarkInputRef.current) {
        watermarkInputRef.current.value = '';
      }
    }
  };

  const handleDeleteWatermark = async () => {
    if (!window.confirm('Are you sure you want to delete the PDF watermark?')) return;

    setError(null);
    setUploadingWatermark(true);

    try {
      await settingsAPI.deletePdfWatermark();
      setSettings(prev => ({ ...prev, pdfWatermark: null }));
      setSuccess('PDF watermark deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete watermark');
    } finally {
      setUploadingWatermark(false);
    }
  };

  if (loading) return <Loading message="Loading settings..." />;

  const renderBrandingTab = () => (
    <div className="tab-content">
      {/* Logo Section */}
      <div className="settings-section">
        <div className="section-header">
          <h3>Logo</h3>
          <p>Upload your organization logo for the navigation bar</p>
        </div>
        <div className="upload-area">
          <div className="preview-box logo-preview-box">
            {settings.logo ? (
              <img src={settings.logo} alt="Logo" />
            ) : (
              <div className="preview-placeholder">
                <span className="placeholder-icon">🖼️</span>
                <span>No logo uploaded</span>
              </div>
            )}
          </div>
          <div className="upload-controls">
            <input
              type="file"
              ref={fileInputRef}
              accept="image/jpeg,image/jpg,image/png,image/svg+xml,image/webp"
              onChange={handleLogoUpload}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : settings.logo ? 'Change Logo' : 'Upload Logo'}
            </button>
            {settings.logo && (
              <button
                type="button"
                className="btn btn-outline btn-danger-outline"
                onClick={handleDeleteLogo}
                disabled={uploading}
              >
                Delete
              </button>
            )}
            <p className="upload-hint">JPEG, PNG, SVG, WebP - Max 5MB</p>
          </div>
        </div>
      </div>

      {/* Favicon Section */}
      <div className="settings-section">
        <div className="section-header">
          <h3>Favicon</h3>
          <p>Browser tab icon (32x32 or 64x64 pixels recommended)</p>
        </div>
        <div className="upload-area">
          <div className="preview-box favicon-preview-box">
            {settings.favicon ? (
              <img src={settings.favicon} alt="Favicon" />
            ) : (
              <div className="preview-placeholder">
                <span className="placeholder-icon">🌐</span>
                <span>No favicon</span>
              </div>
            )}
          </div>
          <div className="upload-controls">
            <input
              type="file"
              ref={faviconInputRef}
              accept="image/x-icon,image/vnd.microsoft.icon,image/png,image/jpeg,image/jpg,image/svg+xml"
              onChange={handleFaviconUpload}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => faviconInputRef.current?.click()}
              disabled={uploadingFavicon}
            >
              {uploadingFavicon ? 'Uploading...' : settings.favicon ? 'Change Favicon' : 'Upload Favicon'}
            </button>
            {settings.favicon && (
              <button
                type="button"
                className="btn btn-outline btn-danger-outline"
                onClick={handleDeleteFavicon}
                disabled={uploadingFavicon}
              >
                Delete
              </button>
            )}
            <p className="upload-hint">ICO, PNG, JPEG, SVG - Max 1MB</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPdfTab = () => (
    <div className="tab-content">
      {/* Lab Assistant Section */}
      <div className="settings-section">
        <div className="section-header">
          <h3>Lab Assistant (Lab Technician)</h3>
          <p>Name and signature for the left side of PDF reports</p>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Name</label>
            <input
              type="text"
              name="labAssistantName"
              className="form-input"
              value={settings.labAssistantName || ''}
              onChange={handleInputChange}
              placeholder="Enter Lab Assistant name"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Title</label>
            <input
              type="text"
              name="labAssistantTitle"
              className="form-input"
              value={settings.labAssistantTitle || ''}
              onChange={handleInputChange}
              placeholder="e.g., Lab. Assistant"
            />
          </div>
        </div>
        <div className="upload-area">
          <div className="preview-box signature-preview-box">
            {settings.labAssistantSignature ? (
              <img src={settings.labAssistantSignature} alt="Lab Assistant Signature" />
            ) : (
              <div className="preview-placeholder">
                <span className="placeholder-icon">✍️</span>
                <span>No signature</span>
              </div>
            )}
          </div>
          <div className="upload-controls">
            <input
              type="file"
              ref={labAssistantSigRef}
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleLabAssistantSigUpload}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => labAssistantSigRef.current?.click()}
              disabled={uploadingLabAssistantSig}
            >
              {uploadingLabAssistantSig ? 'Uploading...' : settings.labAssistantSignature ? 'Change Signature' : 'Upload Signature'}
            </button>
            {settings.labAssistantSignature && (
              <button
                type="button"
                className="btn btn-outline btn-danger-outline"
                onClick={handleDeleteLabAssistantSig}
                disabled={uploadingLabAssistantSig}
              >
                Delete
              </button>
            )}
            <p className="upload-hint">JPEG, PNG, WebP - Max 2MB (transparent PNG recommended)</p>
          </div>
        </div>
      </div>

      {/* Lab Incharge Section */}
      <div className="settings-section">
        <div className="section-header">
          <h3>Lab Incharge</h3>
          <p>Name and signature for the right side of PDF reports</p>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Name</label>
            <input
              type="text"
              name="labInchargeName"
              className="form-input"
              value={settings.labInchargeName || ''}
              onChange={handleInputChange}
              placeholder="Enter Lab Incharge name"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Title</label>
            <input
              type="text"
              name="labInchargeTitle"
              className="form-input"
              value={settings.labInchargeTitle || ''}
              onChange={handleInputChange}
              placeholder="e.g., Lab. Incharge"
            />
          </div>
        </div>
        <div className="upload-area">
          <div className="preview-box signature-preview-box">
            {settings.labInchargeSignature ? (
              <img src={settings.labInchargeSignature} alt="Lab Incharge Signature" />
            ) : (
              <div className="preview-placeholder">
                <span className="placeholder-icon">✍️</span>
                <span>No signature</span>
              </div>
            )}
          </div>
          <div className="upload-controls">
            <input
              type="file"
              ref={labInchargeSigRef}
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleLabInchargeSigUpload}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => labInchargeSigRef.current?.click()}
              disabled={uploadingLabInchargeSig}
            >
              {uploadingLabInchargeSig ? 'Uploading...' : settings.labInchargeSignature ? 'Change Signature' : 'Upload Signature'}
            </button>
            {settings.labInchargeSignature && (
              <button
                type="button"
                className="btn btn-outline btn-danger-outline"
                onClick={handleDeleteLabInchargeSig}
                disabled={uploadingLabInchargeSig}
              >
                Delete
              </button>
            )}
            <p className="upload-hint">JPEG, PNG, WebP - Max 2MB (transparent PNG recommended)</p>
          </div>
        </div>
      </div>

      {/* PDF Watermark Section */}
      <div className="settings-section">
        <div className="section-header">
          <h3>PDF Watermark / Background</h3>
          <p>Background logo that appears behind PDF report content</p>
        </div>
        <div className="upload-area">
          <div className="preview-box watermark-preview-box">
            {settings.pdfWatermark ? (
              <img src={settings.pdfWatermark} alt="PDF Watermark" />
            ) : (
              <div className="preview-placeholder">
                <span className="placeholder-icon">💧</span>
                <span>No watermark</span>
              </div>
            )}
          </div>
          <div className="upload-controls">
            <input
              type="file"
              ref={watermarkInputRef}
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleWatermarkUpload}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => watermarkInputRef.current?.click()}
              disabled={uploadingWatermark}
            >
              {uploadingWatermark ? 'Uploading...' : settings.pdfWatermark ? 'Change Watermark' : 'Upload Watermark'}
            </button>
            {settings.pdfWatermark && (
              <button
                type="button"
                className="btn btn-outline btn-danger-outline"
                onClick={handleDeleteWatermark}
                disabled={uploadingWatermark}
              >
                Delete
              </button>
            )}
            <p className="upload-hint">JPEG, PNG, WebP - Max 5MB (transparent PNG recommended)</p>
          </div>
        </div>
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save PDF Settings'}
        </button>
      </div>
    </div>
  );

  const renderGeneralTab = () => (
    <form onSubmit={handleSave} className="tab-content">
      <div className="settings-section">
        <div className="section-header">
          <h3>Application Information</h3>
          <p>Basic details about your organization</p>
        </div>

        <div className="form-group">
          <label className="form-label">Application Name</label>
          <input
            type="text"
            name="appName"
            className="form-input"
            value={settings.appName || ''}
            onChange={handleInputChange}
            placeholder="Earth Warrior"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Tagline</label>
          <input
            type="text"
            name="tagline"
            className="form-input"
            value={settings.tagline || ''}
            onChange={handleInputChange}
            placeholder="Water Quality Testing Platform"
          />
        </div>
      </div>

      <div className="settings-section">
        <div className="section-header">
          <h3>Contact Information</h3>
          <p>Contact details displayed on reports</p>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              name="contactEmail"
              className="form-input"
              value={settings.contactEmail || ''}
              onChange={handleInputChange}
              placeholder="contact@example.com"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Phone</label>
            <input
              type="text"
              name="contactPhone"
              className="form-input"
              value={settings.contactPhone || ''}
              onChange={handleInputChange}
              placeholder="+91-9876543210"
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Website</label>
          <input
            type="text"
            name="website"
            className="form-input"
            value={settings.website || ''}
            onChange={handleInputChange}
            placeholder="www.example.com"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Address</label>
          <textarea
            name="address"
            className="form-input form-textarea"
            value={settings.address || ''}
            onChange={handleInputChange}
            placeholder="Enter your organization address"
            rows={3}
          />
        </div>
      </div>

      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </form>
  );

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1>Settings</h1>
        <p>Manage application settings and branding</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="settings-container">
        {/* Tab Navigation */}
        <div className="tabs-nav">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="settings-card">
          {activeTab === 'branding' && renderBrandingTab()}
          {activeTab === 'pdf' && renderPdfTab()}
          {activeTab === 'general' && renderGeneralTab()}
        </div>
      </div>
    </div>
  );
};

export default Settings;
